import styles from './contacto-page.module.scss';
import React from "react";
import { Link } from "react-router-dom";

import Hero from "../../components/hero/hero";
import { HERO_WAVE_PAGE } from "../../components/hero/waves";
import api from "../../services/api";

export interface ContactoPageProps {
  name?: string;
  classes?: string;
}

interface State {
  sending: boolean;
  sent: boolean;
  error?: string;
}

const faqs = [
  {
    q: "¿Tienen envíos?",
    a: "Sí, manejamos entregas en…",
  },
  {
    q: "¿Dónde puedo ver el catálogo completo?",
    a: "En nuestra sección de Productos…",
  },
  {
    q: "¿Cómo hago un pedido?",
    a: "Escríbenos por WhatsApp y te atendemos…",
  },
];

const LeafIcon = (
  <svg className={styles['mb-faq__icon']} viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M20 3c-8 0-14 4-14 11 0 1.7.4 3.2 1.2 4.4L5 21.5l1.4 1.4 2.9-2.9C12.3 21 14 21.5 15.5 21.5 20 21.5 22 15 22 6c0-1.2-.1-2.2-.2-3h-1.8Zm-3.6 13.9c-1.9.3-4-.7-5.4-2.3 3.5-2.6 6-6.1 6.6-9.6.9 3.9.5 10.9-1.2 11.9Z"
    />
  </svg>
);

/**
 * Contacto (Figma `110:398`, 1920×5091):
 * Hero · intro · banda 24h + mapa · formulario · FAQ + imagen + Ver productos.
 */
export default class ContactoPage extends React.Component<ContactoPageProps, State> {
  static jsClass = "ContactoPage";
  static defaultProps: Partial<ContactoPageProps> = {
    name: "mbContacto",
  };

  state: State = { sending: false, sent: false };
  private formRef = React.createRef<HTMLFormElement>();

  private onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = this.formRef.current;
    if (!form) return;
    const data = new FormData(form);
    this.setState({ sending: true, error: undefined });
    api
      .contact({
        name: String(data.get("nombre") || ""),
        email: String(data.get("correo") || ""),
        phone: String(data.get("telefono") || ""),
        message: String(data.get("mensaje") || ""),
      })
      .then(() => {
        form.reset();
        this.setState({ sending: false, sent: true });
      })
      .catch((err) => this.setState({ sending: false, error: String(err?.message ?? err) }));
  };

  render(): React.ReactNode {
    const { classes } = this.props;
    const { sending, sent, error } = this.state;

    return (
      <div className={styles['mb-home'] + (classes ? ' ' + classes : '')}>
        <Hero
          src="/assets/design/contacto-vec1.webp"
          title="Contacto"
          align="end"
          wave={HERO_WAVE_PAGE}
        />

        {/* ── Intro ───────────────────────────────────────────────────────── */}
        <section className={styles['mb-intro']}>
          <div className={styles['mb-wrap']}>
            <p className={styles['mb-intro__copy']}>
              En Mangibone estamos para atenderte. Si tienes alguna duda, pedido o
              deseas más información, contáctanos por cualquiera de nuestros canales
              y con gusto te apoyamos.
            </p>
          </div>
        </section>

        {/* ── Banda 24h + mapa ───────────────────────────────────────────── */}
        <section className={styles['mb-24h']}>
          <div className={styles['mb-wrap']}>
            <p className={styles['mb-24h__text']}>
              Respondemos mensajes en un máximo de 24 horas.
              <br />
              WhatsApp tiene respuesta más rápida.
            </p>
          </div>
          <img
            className={styles['mb-24h__map']}
            src="/assets/design/contacto-rect34.webp"
            alt="Ubicación de Mangibone"
          />
        </section>

        {/* ── Formulario ─────────────────────────────────────────────────── */}
        <section className={styles['mb-form']}>
          <div className={styles['mb-wrap']}>
            <form
              ref={this.formRef}
              className={styles['mb-form__grid']}
              onSubmit={this.onSubmit}>
              <div className={styles['mb-form__fields']}>
                <input className={styles['mb-form__input']} type="text" name="nombre" placeholder="Nombre" aria-label="Nombre" />
                <input className={styles['mb-form__input']} type="tel" name="telefono" placeholder="Teléfono" aria-label="Teléfono" />
                <input className={styles['mb-form__input']} type="email" name="correo" placeholder="Correo" aria-label="Correo" />
                <input className={styles['mb-form__input']} type="text" name="mensaje" placeholder="Mensaje" aria-label="Mensaje" />
              </div>
              <button className={styles['mb-form__btn']} type="submit" disabled={sending}>
                {sending ? "Enviando…" : "Enviar mensaje"}
              </button>
            </form>
            {sent && (
              <p className={styles['mb-form__feedback']}>
                ¡Gracias! Tu mensaje fue enviado, te contactaremos pronto.
              </p>
            )}
            {error && (
              <p className={styles['mb-form__feedback']}>
                No se pudo enviar el mensaje ({error}). Intenta de nuevo.
              </p>
            )}
          </div>
        </section>

        {/* ── FAQ + imagen + Ver productos ───────────────────────────────── */}
        <section className={styles['mb-faq']}>
          <div className={styles['mb-wrap']}>
            <div className={styles['mb-faq__grid']}>
              <ul className={styles['mb-faq__items']}>
                {faqs.map((f) => (
                  <li className={styles['mb-faq__item']} key={f.q}>
                    {LeafIcon}
                    <span>
                      <strong className={styles['mb-faq__q']}>{f.q}</strong>
                      <em className={styles['mb-faq__a']}>{f.a}</em>
                    </span>
                  </li>
                ))}
              </ul>
              <div className={styles['mb-faq__media']}>
                <img
                  src="/assets/design/contacto-rect39.webp"
                  alt="Mangibone — productos artesanales"
                />
                <Link className={styles['mb-faq__btn']} to="/productos">
                  Ver productos
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }
}
